import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CartSidebar from '../../components/feature/CartSidebar';
import SearchModal from '../../components/feature/SearchModal';
import LoginModal from '../../components/feature/LoginModal';
import ReviewModal from './components/ReviewModal';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { SITE_ID, CURRENCY_SYMBOL } from '../../lib/constants';

interface OrderProduct {
  urun_id?: string;
  urun_adi: string;
  birim_fiyat: number;
  adet: number;
  toplam: number;
  gorsel?: string;
  varyant?: string;
  // legacy field support
  name?: string;
  price?: number;
  quantity?: number;
  image_url?: string;
}

interface Order {
  id: string;
  siparis_no: string;
  musteri_adi: string;
  urunler: OrderProduct[];
  ara_toplam: number;
  kargo_ucreti: number;
  indirim: number;
  toplam_tutar: number;
  durum: string;
  odeme_durumu: string;
  odeme_yontemi: string;
  kargo_takip_no: string | null;
  kargo_firmasi: string | null;
  olusturma_tarihi: string;
}

const STATUS_MAP: Record<string, { label: string; color: string; icon: string }> = {
  beklemede: { label: 'Beklemede', color: 'bg-amber-50 text-amber-700', icon: 'ri-time-line' },
  hazirlaniyor: { label: 'Hazırlanıyor', color: 'bg-blue-50 text-blue-700', icon: 'ri-settings-3-line' },
  kargoda: { label: 'Kargoda', color: 'bg-indigo-50 text-indigo-700', icon: 'ri-truck-line' },
  teslim_edildi: { label: 'Teslim Edildi', color: 'bg-green-50 text-green-700', icon: 'ri-checkbox-circle-line' },
  tamamlandi: { label: 'Tamamlandı', color: 'bg-green-50 text-green-700', icon: 'ri-checkbox-circle-line' },
  iptal: { label: 'İptal Edildi', color: 'bg-red-50 text-red-700', icon: 'ri-close-circle-line' },
};

// Yorum yapılabilir sipariş durumları
const REVIEWABLE_STATUSES = ['teslim_edildi', 'tamamlandi'];

export default function OrdersPage() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [reviewProduct, setReviewProduct] = useState<{ product: OrderProduct; orderId: string } | null>(null);
  // key: "siparisId__urunId" → aynı ürün farklı siparişlerde bağımsız değerlendirilebilir
  const [reviewedKeys, setReviewedKeys] = useState<Set<string>>(new Set());
  const { user } = useAuth();

  useEffect(() => {
    if (!user) { setLoading(false); return; }

    async function fetchOrders() {
      setLoading(true);
      const { data } = await supabase
        .from('siparisler')
        .select('*')
        .eq('site_id', SITE_ID)
        .eq('musteri_email', user!.email)
        .order('olusturma_tarihi', { ascending: false });

      setOrders((data as Order[]) || []);
      setLoading(false);
    }

    fetchOrders();
  }, [user]);

  // Kullanıcının yaptığı yorumları çek
  // Her urun_id için kaç yorum yapılmış say → siparişlerdeki o ürün sayısıyla karşılaştır
  useEffect(() => {
    if (!user || orders.length === 0) return;
    async function fetchUserReviews() {
      const { data } = await supabase
        .from('urun_yorumlari')
        .select('urun_id')
        .eq('site_id', SITE_ID)
        .eq('kullanici_id', user.id);

      if (!data) return;

      // Her urun_id için yapılan yorum sayısını say
      const reviewCountMap = new Map<string, number>();
      for (const r of data as { urun_id: string }[]) {
        reviewCountMap.set(r.urun_id, (reviewCountMap.get(r.urun_id) || 0) + 1);
      }

      // Siparişleri eskiden yeniye sırala
      const sorted = [...orders].sort(
        (a, b) => new Date(a.olusturma_tarihi).getTime() - new Date(b.olusturma_tarihi).getTime()
      );

      // Her urun_id için kalan yorum hakkını tüket: eski siparişlerden başla
      const remainingReviews = new Map<string, number>(reviewCountMap);
      const keys = new Set<string>();

      for (const order of sorted) {
        const prods: OrderProduct[] = Array.isArray(order.urunler) ? order.urunler : [];
        for (const p of prods) {
          if (!p.urun_id) continue;
          const remaining = remainingReviews.get(p.urun_id) || 0;
          if (remaining > 0) {
            keys.add(`${order.id}__${p.urun_id}`);
            remainingReviews.set(p.urun_id, remaining - 1);
          }
        }
      }

      setReviewedKeys(keys);
    }
    fetchUserReviews();
  }, [user, orders]);

  const getStatus = (durum: string) =>
    STATUS_MAP[durum] || { label: durum, color: 'bg-gray-50 text-gray-600', icon: 'ri-question-line' };

  return (
    <div className="min-h-screen bg-cream-light font-sans">
      <Navbar onSearchOpen={() => setSearchOpen(true)} onLoginOpen={() => setLoginOpen(true)} />

      <main className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-4 lg:px-8">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <Link to="/profil" className="w-9 h-9 flex items-center justify-center rounded-xl bg-white hover:bg-cream transition-colors cursor-pointer">
              <i className="ri-arrow-left-line text-coffee" />
            </Link>
            <div>
              <h1 className="font-serif text-2xl font-bold text-coffee">Siparişlerim</h1>
              <p className="text-xs text-coffee-light mt-0.5">Tüm sipariş geçmişin</p>
            </div>
          </div>

          {!user ? (
            <div className="bg-white rounded-3xl p-12 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-cream rounded-full mx-auto mb-4">
                <i className="ri-shopping-bag-line text-2xl text-coffee-light" />
              </div>
              <p className="text-coffee-light mb-6 text-sm">Siparişlerini görmek için giriş yapman gerekiyor.</p>
              <button
                onClick={() => setLoginOpen(true)}
                className="px-6 py-3 bg-coffee text-white font-semibold rounded-xl hover:bg-coffee-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Giriş Yap
              </button>
            </div>
          ) : loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                  <div className="flex justify-between mb-3">
                    <div className="h-4 bg-cream rounded w-32" />
                    <div className="h-6 bg-cream rounded-full w-24" />
                  </div>
                  <div className="h-3 bg-cream rounded w-48 mb-2" />
                  <div className="h-3 bg-cream rounded w-24" />
                </div>
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-cream rounded-full mx-auto mb-5">
                <i className="ri-shopping-bag-line text-3xl text-coffee-light" />
              </div>
              <h2 className="font-serif text-xl font-bold text-coffee mb-2">Henüz sipariş yok</h2>
              <p className="text-coffee-light text-sm mb-6">İlk siparişini vermek için ürünlere göz at.</p>
              <Link
                to="/urunler"
                className="inline-block px-6 py-3 bg-coffee text-white font-semibold rounded-xl hover:bg-coffee-medium transition-colors whitespace-nowrap"
              >
                Ürünleri Keşfet
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map(order => {
                const status = getStatus(order.durum);
                const isExpanded = expandedId === order.id;
                const products: OrderProduct[] = Array.isArray(order.urunler) ? order.urunler : [];

                // Teslim edilmiş / tamamlanmış siparişlerde değerlendirilmemiş ürün var mı?
                const reviewableProducts = REVIEWABLE_STATUSES.includes(order.durum)
                  ? products.filter(p => p.urun_id && !reviewedKeys.has(`${order.id}__${p.urun_id}`))
                  : [];
                const hasReviewable = reviewableProducts.length > 0;

                return (
                  <div key={order.id} className="bg-white rounded-2xl overflow-hidden">
                    {/* Order Header */}
                    <div className="px-6 py-5 flex items-center justify-between hover:bg-cream/40 transition-colors">
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : order.id)}
                        className="flex-1 text-left cursor-pointer"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm text-coffee">#{order.siparis_no}</span>
                          <span className={`flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full font-medium ${status.color}`}>
                            <i className={status.icon} />
                            {status.label}
                          </span>
                        </div>
                        <p className="text-xs text-coffee-light">
                          {new Date(order.olusturma_tarihi).toLocaleDateString('tr-TR', {
                            year: 'numeric', month: 'long', day: 'numeric',
                          })}
                          {' · '}
                          {products.reduce((sum, p) => sum + (p.adet ?? p.quantity ?? 1), 0)} ürün ({products.length} çeşit)
                        </p>
                      </button>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {hasReviewable && (
                          <button
                            onClick={() => setReviewProduct({ product: reviewableProducts[0], orderId: order.id })}
                            className="flex items-center gap-1.5 text-xs font-medium text-gold hover:text-gold-dark bg-gold/10 hover:bg-gold/20 px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-star-line" />
                            Değerlendir
                            {reviewableProducts.length > 1 && (
                              <span className="bg-gold text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                                {reviewableProducts.length}
                              </span>
                            )}
                          </button>
                        )}
                        <span className="font-bold text-coffee text-sm">
                          {CURRENCY_SYMBOL}{Number(order.toplam_tutar).toLocaleString('tr-TR')}
                        </span>
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : order.id)}
                          className="cursor-pointer"
                        >
                          <i className={`text-coffee-light transition-transform ${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
                        </button>
                      </div>
                    </div>

                    {/* Order Detail */}
                    {isExpanded && (
                      <div className="border-t border-cream-dark px-6 py-5 space-y-4">
                        {/* Products */}
                        <div className="space-y-3">
                          {products.map((p, idx) => {
                            const productName = p.urun_adi || p.name || 'Ürün';
                            const productQty = p.adet ?? p.quantity ?? 1;
                            const productPrice = p.birim_fiyat ?? p.price ?? 0;
                            const productTotal = p.toplam ?? (productPrice * productQty);
                            const productImage = p.gorsel || p.image_url;
                            const productId = p.urun_id || '';
                            const isReviewed = reviewedKeys.has(`${order.id}__${productId}`);
                            const canReview = REVIEWABLE_STATUSES.includes(order.durum) && productId && !isReviewed;

                            return (
                            <div key={idx} className="flex items-center gap-3">
                              {productImage ? (
                                <img src={productImage} alt={productName} className="w-12 h-12 rounded-xl object-cover object-top flex-shrink-0" />
                              ) : (
                                <div className="w-12 h-12 rounded-xl bg-cream flex items-center justify-center flex-shrink-0">
                                  <i className="ri-cup-line text-coffee-light" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-coffee truncate">{productName}</p>
                                {p.varyant && <p className="text-xs text-coffee-light truncate">{p.varyant}</p>}
                                <p className="text-xs text-coffee-light">{productQty} adet · {CURRENCY_SYMBOL}{productPrice.toLocaleString('tr-TR')} / adet</p>
                              </div>
                              <div className="flex flex-col items-end gap-1">
                                <span className="text-sm font-semibold text-coffee whitespace-nowrap">
                                  {CURRENCY_SYMBOL}{productTotal.toLocaleString('tr-TR')}
                                </span>
                                {canReview && (
                                  <button
                                    onClick={() => setReviewProduct({ product: p, orderId: order.id })}
                                    className="text-xs text-gold hover:text-gold-dark font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
                                  >
                                    <i className="ri-star-line" />
                                    Değerlendir
                                  </button>
                                )}
                                {isReviewed && (
                                  <span className="text-xs text-green-600 flex items-center gap-1">
                                    <i className="ri-check-line" />
                                    Değerlendirildi
                                  </span>
                                )}
                              </div>
                            </div>
                            );
                          })}
                        </div>

                        {/* Price Summary */}
                        <div className="border-t border-cream-dark pt-4 space-y-1.5">
                          <div className="flex justify-between text-xs text-coffee-light">
                            <span>Ara Toplam</span>
                            <span>{CURRENCY_SYMBOL}{Number(order.ara_toplam).toLocaleString('tr-TR')}</span>
                          </div>
                          {Number(order.kargo_ucreti) > 0 && (
                            <div className="flex justify-between text-xs text-coffee-light">
                              <span>Kargo</span>
                              <span>{CURRENCY_SYMBOL}{Number(order.kargo_ucreti).toLocaleString('tr-TR')}</span>
                            </div>
                          )}
                          {Number(order.indirim) > 0 && (
                            <div className="flex justify-between text-xs text-green-600">
                              <span>İndirim</span>
                              <span>-{CURRENCY_SYMBOL}{Number(order.indirim).toLocaleString('tr-TR')}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-sm font-bold text-coffee pt-1 border-t border-cream-dark">
                            <span>Toplam</span>
                            <span>{CURRENCY_SYMBOL}{Number(order.toplam_tutar).toLocaleString('tr-TR')}</span>
                          </div>
                        </div>

                        {/* Cargo Info */}
                        {order.kargo_takip_no && (
                          <div className="bg-cream rounded-xl px-4 py-3 flex items-center gap-3">
                            <div className="w-8 h-8 flex items-center justify-center">
                              <i className="ri-truck-line text-gold text-lg" />
                            </div>
                            <div>
                              <p className="text-xs font-medium text-coffee">
                                {order.kargo_firmasi || 'Kargo'} · Takip No
                              </p>
                              <p className="text-xs text-coffee-light font-mono">{order.kargo_takip_no}</p>
                            </div>
                          </div>
                        )}

                        {/* Payment */}
                        <div className="flex items-center gap-2 text-xs text-coffee-light">
                          <i className="ri-bank-card-line" />
                          <span>
                            {order.odeme_yontemi === 'kapida_odeme' ? 'Kapıda Ödeme' :
                             order.odeme_yontemi === 'havale' ? 'Havale / EFT' :
                             order.odeme_yontemi === 'kredi_karti' ? 'Kredi Kartı' :
                             order.odeme_yontemi || 'Belirtilmemiş'}
                          </span>
                          <span className="mx-1">·</span>
                          <span className={order.odeme_durumu === 'odendi' ? 'text-green-600' : 'text-amber-600'}>
                            {order.odeme_durumu === 'odendi' ? 'Ödendi' :
                             order.odeme_durumu === 'beklemede' ? 'Ödeme Bekleniyor' :
                             order.odeme_durumu || ''}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <Footer />
      <CartSidebar />
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} initialMode="login" />
      <ReviewModal
        isOpen={!!reviewProduct}
        onClose={() => setReviewProduct(null)}
        product={reviewProduct?.product || { urun_adi: '' }}
        onSubmitted={() => {
          if (reviewProduct?.product?.urun_id && reviewProduct?.orderId) {
            const key = `${reviewProduct.orderId}__${reviewProduct.product.urun_id}`;
            setReviewedKeys(prev => new Set([...prev, key]));
          }
        }}
      />
    </div>
  );
}
